// First-party, privacy-friendly analytics client (no cookies, no PII).
// Sends pageview / duration / click events to the CarLens backend.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_TTL = 30 * 60 * 1000; // 30 min inactivity = new session

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getVisitorId() {
  try {
    let v = localStorage.getItem('cl_vid');
    if (!v) { v = uid(); localStorage.setItem('cl_vid', v); }
    return v;
  } catch (e) { return 'anon'; }
}

function getSessionId() {
  try {
    const now = Date.now();
    let sid = sessionStorage.getItem('cl_sid');
    const last = parseInt(sessionStorage.getItem('cl_sid_ts') || '0', 10);
    if (!sid || (now - last) > SESSION_TTL) {
      sid = uid();
      sessionStorage.setItem('cl_sid', sid);
      sessionStorage.removeItem('cl_entry'); // new session → next pageview is entry
    }
    sessionStorage.setItem('cl_sid_ts', String(now));
    return sid;
  } catch (e) { return 'anon'; }
}

function isSessionEntry() {
  try {
    if (!sessionStorage.getItem('cl_entry')) {
      sessionStorage.setItem('cl_entry', '1');
      return true;
    }
    return false;
  } catch (e) { return false; }
}

function trafficSource() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = params.get('utm_source');
    if (utm) return utm.toLowerCase().slice(0, 40);
    const ref = document.referrer;
    if (!ref) return 'direct';
    const host = new URL(ref).hostname.replace(/^www\./, '');
    if (host === window.location.hostname) return 'internal';
    if (host.includes('google')) return 'google';
    if (host.includes('facebook') || host === 'fb.com' || host.includes('fb.')) return 'facebook';
    if (host.includes('instagram')) return 'instagram';
    if (host.includes('bing')) return 'bing';
    if (host.includes('duckduckgo')) return 'duckduckgo';
    if (host.includes('yahoo')) return 'yahoo';
    if (host === 't.co' || host.includes('twitter') || host === 'x.com') return 'twitter';
    return host.slice(0, 40);
  } catch (e) { return 'direct'; }
}

function send(payload, useBeacon) {
  try {
    const body = JSON.stringify({
      ...payload,
      visitorId: getVisitorId(),
      sessionId: getSessionId(),
    });
    const url = `${API}/analytics/event`;
    // Beacon is only reliable for unload; for normal events use keepalive fetch.
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => {});
    }
  } catch (e) { /* never break the app for analytics */ }
}

let currentPath = null;
let pageEnter = 0;
let lastPvKey = null;
let lastPvTs = 0;

function flushDuration() {
  if (currentPath !== null) {
    const ms = Date.now() - pageEnter;
    if (ms > 0) send({ type: 'duration', path: currentPath, durationMs: ms }, true);
  }
}

export function trackPageview(path) {
  if (path && path.startsWith('/admin')) return; // don't track owner admin usage
  const now = Date.now();
  // Dedupe rapid duplicate fires (e.g. React StrictMode double-invoking effects in dev)
  if (path === lastPvKey && (now - lastPvTs) < 1500) return;
  lastPvKey = path;
  lastPvTs = now;
  flushDuration();
  currentPath = path;
  pageEnter = now;
  send({
    type: 'pageview',
    path,
    referrer: document.referrer || '',
    source: trafficSource(),
    isEntry: isSessionEntry(),
    title: (document.title || '').slice(0, 120),
  });
}

function labelForClick(el) {
  const t = el.closest('[data-testid]');
  if (t && t.getAttribute('data-testid')) return t.getAttribute('data-testid');
  const txt = (el.textContent || '').trim().replace(/\s+/g, ' ');
  return txt ? txt.slice(0, 60) : el.tagName.toLowerCase();
}

let listenersBound = false;

export function initAnalytics() {
  if (listenersBound || typeof window === 'undefined') return;
  listenersBound = true;

  // Track clicks on interactive elements (buttons, links, role=button)
  document.addEventListener('click', (e) => {
    try {
      const el = e.target.closest('a, button, [role="button"]');
      if (!el) return;
      const path = window.location.pathname;
      if (path.startsWith('/admin')) return;
      send({ type: 'click', path, label: labelForClick(el) });
    } catch (err) { /* ignore */ }
  }, true);

  // Flush time-on-page when the tab is hidden or the user leaves
  const flush = () => flushDuration();
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}
